<template>
  <!-- Tooltip flotante genérico (chrome del visor). Position fixed; x/y es el
       punto ANCLA (normalmente el puntero). La tarjeta se coloca DEBAJO con la cola
       sobre ese punto; si no cabe debajo se voltea ARRIBA, y si se saldría por los
       lados se acota al viewport (clamp) — la cola sigue al puntero dentro de la
       tarjeta. Cada fila es { label, color? }: `color` pinta un swatch. `note` =
       línea atenuada. Tema claro/azul de marca, coherente con el callout. Solo
       Tailwind, sin CSS. -->
  <div
    v-if="visible"
    ref="card"
    :style="{ left: placement.left + 'px', top: placement.top + 'px' }"
    class="fixed z-[9999] pointer-events-none whitespace-nowrap py-2 px-[11px] rounded-[10px]
           text-xs text-gray-700 bg-white border border-[#3a6bc8]/35
           shadow-[0_10px_28px_rgba(17,24,39,0.22),0_3px_8px_rgba(17,24,39,0.14)]"
  >
    <!-- Cola de bocadillo apuntando al ancla: cuadradito girado 45° con bg + los
         dos bordes exteriores de la tarjeta. Arriba (border-t/l) cuando la tarjeta
         va debajo; abajo (border-b/r) cuando se voltea. Absolute → fuera del
         space-y del contenido. -->
    <span
      class="absolute w-2 h-2 rotate-45 bg-white border-[#3a6bc8]/35"
      :class="placement.above ? '-bottom-1 border-b border-r' : '-top-1 border-t border-l'"
      :style="{ left: placement.tailLeft + 'px' }"></span>

    <div class="space-y-1">
      <div v-if="title" class="font-semibold text-gray-800">{{ title }}</div>

      <div v-for="(row, i) in rows" :key="i" class="flex items-center gap-1.5">
        <span v-if="row.color"
          class="inline-block w-3 h-3 rounded-sm border border-gray-300"
          :style="{ backgroundColor: row.color }"></span>
        <span>{{ row.label }}</span>
      </div>

      <div v-if="note" class="text-gray-400 italic">{{ note }}</div>
    </div>
  </div>
</template>

<script>
// Geometría del anclaje (px). GAP = separación vertical puntero↔tarjeta;
// TAIL_INSET = distancia del borde izquierdo al centro de la cola; MARGIN =
// respiro contra los bordes del viewport; HALF = media cola (w-2 = 8px).
const GAP = 10
const TAIL_INSET = 16
const MARGIN = 8
const HALF = 4

export default {
  name: "WidgetHoverTooltip",

  props: {
    visible: { type: Boolean, default: false },
    // Punto ancla en px (normalmente las coords del puntero en el viewport).
    x: { type: Number, default: 0 },
    y: { type: Number, default: 0 },
    // Cabecera en negrita (opcional).
    title: { type: String, default: "" },
    // Filas: [{ label, color? }]. `color` → swatch antes del label.
    rows: { type: Array, default: () => [] },
    // Línea secundaria atenuada (opcional).
    note: { type: String, default: "" },
  },

  data() {
    // Tamaño real de la tarjeta, medido tras renderizar (clamp al viewport).
    return { cardW: 0, cardH: 0 }
  },

  computed: {
    /**
     * Posición acotada al viewport + lado de la cola.
     * - X: cola centrada bajo el ancla; si la tarjeta se saldría, se empuja dentro
     *   y la cola se mantiene bajo el puntero (dentro de los límites de la tarjeta).
     * - Y: debajo por defecto; si no cabe y arriba sí, se voltea.
     */
    placement() {
      const vw = (typeof window !== 'undefined' && window.innerWidth) || 0
      const vh = (typeof window !== 'undefined' && window.innerHeight) || 0

      // Horizontal: ancla la cola bajo el puntero y acota al viewport.
      let left = this.x - TAIL_INSET
      const maxLeft = Math.max(MARGIN, vw - this.cardW - MARGIN)
      left = Math.min(Math.max(MARGIN, left), maxLeft)

      // Vertical: debajo salvo que no quepa y arriba sí.
      const fitsBelow = (this.y + GAP + this.cardH) <= (vh - MARGIN)
      const fitsAbove = (this.y - GAP - this.cardH) >= MARGIN
      const above = !fitsBelow && fitsAbove
      const top = above ? (this.y - GAP - this.cardH) : (this.y + GAP)

      // Cola: bajo el puntero, pero sin salirse de la tarjeta.
      let tailCenter = this.x - left
      tailCenter = Math.min(Math.max(12, tailCenter), Math.max(12, this.cardW - 12))

      return { left, top, above, tailLeft: tailCenter - HALF }
    },
  },

  mounted() {
    this._measure()
  },

  updated() {
    this._measure()
  },

  methods: {
    // Mide la tarjeta y guarda su tamaño solo si cambió (evita bucle de updated).
    _measure() {
      const el = this.$refs.card
      if (!el) return
      if (el.offsetWidth !== this.cardW) this.cardW = el.offsetWidth
      if (el.offsetHeight !== this.cardH) this.cardH = el.offsetHeight
    },
  },
}
</script>
